import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { Organization, Sku } from "../api/models"
import { requiredMark, skuNameValid } from "./InstallerFields"
import { ImageFieldView, useImageField } from "./ImageField"
import { SKU_LOGO_FIELD_HINT, useSkuLogoField } from "./LogoPlacementDialog"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// shared by the version dialogs' numeric claim fields (historically defined
// here, kept exported to avoid churning their imports)
export const optionalPositiveInt = (text: string) =>
  text === "" || (Number.isInteger(Number(text)) && Number(text) > 0)

// Create a brand-new sku — deliberately MINIMAL: identity and store metadata
// only (name, description, highlights, code prefix, screenshot, optional
// logo, owner). No
// versions, no claims, no installers: the sku starts unshipped and
// versionless, and everything licensing — the first draft version with its
// plug-in set, credits and installers — is added afterwards through the SKU
// page's Add Version wizard, which is purpose-built for it. Images are
// keyed by the sku's id, so saving is create → upload → patch: if an upload
// fails the sku already exists — the dialog says so, locks the other
// fields, and offers a retry of the images only (closing hands the created
// sku to onCreated all the same). Conditionally mounted (OfferingDialog
// pattern).
export default function AddSkuDialog({ accessToken, isAdmin, onClose, onCreated }: {
  accessToken: string
  isAdmin: boolean
  onClose: () => void
  onCreated: (sku: Sku) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [highlightsText, setHighlightsText] = useState("")
  const [codePrefix, setCodePrefix] = useState("")
  const screenshot = useImageField(accessToken)
  // system admin only: which ORGANIZATION owns the sku ("" = the backend's
  // default, the creator's own org); members always create in their org
  const [owner, setOwner] = useState("")
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const { logo, editor: logoEditor, adjust: adjustLogo } = useSkuLogoField({ accessToken, ownerId: owner })
  const [step, setStep] = useState<"form" | "saving" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")
  // the sku once it exists; the ref serves the dialog's close handler,
  // which runs before a state update would reach it
  const [created, setCreated] = useState<Sku | null>(null)
  const createdRef = useRef<Sku | null>(null)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  useEffect(() => {
    if (!isAdmin || !accessToken) return
    let stale = false
    Api.listOrganizations({ accessToken })
      .then(({ organizations }) => { if (!stale) setOrganizations(organizations) })
      .catch(() => { })
    return () => { stale = true }
  }, [isAdmin, accessToken])

  const highlights = highlightsText.split("\n").map(h => h.trim()).filter(h => h !== "")

  const formValid =
    skuNameValid(name)
    && description.trim() !== ""
    && highlights.every(h => !h.includes(";"))
    && (codePrefix.trim() === "" || /^[A-Z0-9]{4}$/.test(codePrefix.trim()))

  const handleSubmit = async () => {
    if (!formValid) return
    setStep("saving")
    setErrorMessage("")
    try {
      if (createdRef.current === null) {
        const { sku } = await Api.createSku({
          sku: {
            name: name.trim(),
            description: description.trim(),
            highlights,
            codePrefix: codePrefix.trim() || undefined,
            ownerId: owner || undefined
          },
          accessToken
        })
        createdRef.current = sku
        setCreated(sku)
      }
      const skuId = createdRef.current.id
      const patch: Parameters<typeof Api.updateSku>[0]["patch"] = {}
      if (screenshot.file) {
        const { width, rev } = await screenshot.upload(skuId)
        patch.imageWidth = width
        patch.imageRev = rev
      }
      if (logo.file) {
        const { width, rev } = await logo.upload(skuId)
        patch.logoWidth = width
        patch.logoRev = rev
      }
      if (Object.keys(patch).length > 0)
        createdRef.current = (await Api.updateSku({ skuId, patch, accessToken })).sku
      // closing reports the sku (handleClose)
      dialogRef.current?.close()
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown error"
      setErrorMessage(createdRef.current !== null
        ? `The SKU was created, but adding its images failed: ${message} — retry, or close and add them from Edit.`
        : message)
      setStep("error")
    }
  }

  const handleClose = () => {
    if (createdRef.current !== null) onCreated(createdRef.current)
    onClose()
  }

  const busy = step === "saving"
  // once the sku exists only its images are still open
  const locked = busy || created !== null

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={handleClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content no-scrollbar" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Add SKU</h2>
        <p className="generate-codes-subtitle">
          Just the listing — add the first draft version (plug-ins &
          installers) and offerings from the SKU's page afterwards.
        </p>

        <div className="generate-codes-form">
          <label className="generate-codes-field">
            <span className="generate-codes-label">Name{requiredMark}</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="generate-codes-input"
              disabled={locked}
              autoFocus
            />
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Description{requiredMark}</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="generate-codes-input"
              disabled={locked}
              rows={3}
            />
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Highlights</span>
            <textarea
              value={highlightsText}
              onChange={(e) => setHighlightsText(e.target.value)}
              className="generate-codes-input"
              disabled={locked}
              rows={3}
              placeholder={"one highlight per line"}
            />
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Code prefix</span>
            <input
              type="text"
              value={codePrefix}
              onChange={(e) => setCodePrefix(e.target.value.toUpperCase())}
              className="generate-codes-input"
              disabled={locked}
              maxLength={4}
              placeholder="e.g. ARTC — empty: redemption codes cannot be minted"
            />
            {/* one fixed-height slot: the info line swaps to the error in
                place, so the dialog doesn't grow when it appears */}
            <span style={{
              fontSize: "9pt",
              minHeight: "1.3em",
              color: codePrefix.trim() !== "" && !/^[A-Z0-9]{4}$/.test(codePrefix.trim())
                ? "#e07470" : "var(--nfa-light-grey)"
            }}>
              {codePrefix.trim() !== "" && !/^[A-Z0-9]{4}$/.test(codePrefix.trim())
                ? "The prefix must be exactly 4 uppercase letters/digits — or empty."
                : "Exactly 4 uppercase letters/digits — or leave empty."}
            </span>
          </label>

          {isAdmin && (
            <div className="generate-codes-field">
              <span className="generate-codes-label">Owner organization</span>
              <select
                className="generate-codes-input"
                style={{ width: "auto" }}
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                disabled={locked}
              >
                <option value="">your organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          )}

          <ImageFieldView fields={screenshot} busy={busy} />
          <ImageFieldView
            fields={logo}
            busy={busy}
            label="Logo (PNG, optional)"
            hint={SKU_LOGO_FIELD_HINT}
            variant="logo"
            onAdjust={adjustLogo}
          />
        </div>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={busy}>{created !== null ? "Close" : "Cancel"}</button>
          <button
            className="btn-upgrade"
            disabled={!formValid || busy}
            onClick={handleSubmit}
          >
            {busy ? <><Spinner size={12} /> {created !== null ? "Uploading…" : "Creating…"}</> : created !== null ? "Retry Images" : "Create SKU"}
          </button>
        </div>
      </div>
      {logoEditor}
    </dialog>
  )
}
